import { Ref } from 'vue'
import dataStorageManager, { BucketDefaultIDs, DMS_Operation } from '../../managers/data/DataStorageManager'
import dataQueryManager from '../../managers/network/DataQueryManager'
import { emailify, rutify } from '../apiUtils'
import { IRequest } from './RequestsServices'

export enum NotificationID {
  SOL_INS_ARM = 1,
  SOL_INS_EMB = 2,
  SOL_INS_TRI = 3,
  SOL_INS_PROV = 4,
  SOL_MODIF_ARM = 5,
  SOL_MODIF_EMB = 6,
  SOL_MODIF_TRI = 7,
  SOL_MODIF_PROV = 8,
  SOL_SERVICIO = 9,
  SOL_VIAJE_PREACR = 10,
  NOTIF_SERV_DISPONIBLE = 11,
  NOTIF_VIAJE_DISPONIBLE = 12,
  NOTIF_CAMBIO_MUELLE = 13,
  RESP_INS_ARM = 14,
  RESP_INS_EMB = 15,
  RESP_INS_TRI = 16,
  RESP_INS_PROV = 17,
  RESP_MODIF_ARM = 18,
  RESP_MODIF_EMB = 19,
  RESP_MODIF_TRI = 20,
  RESP_MODIF_PROV = 21,
  RESP_SOL_SERVICIO = 22,
  NOTIF_TARIFA_ESPECIAL = 23,
  RESP_PREACR = 24,
  NOTIF_CAMBIO_MUELLE_2 = 25,
  RESP_SOL_MUELLE_WESTE = 26,
  ALERTA_COBRO = 27,
  SOL_REP = 28,
  RESP_REP = 29,
}

export const NotificationsPageMap = (notification: INotification) => {
  switch (notification.id_notificacion) {
    case NotificationID.SOL_INS_ARM:
    case NotificationID.SOL_MODIF_ARM:
      return 'enrollment-armadores-check'
    case NotificationID.SOL_INS_EMB:
    case NotificationID.SOL_MODIF_EMB:
      return 'enrollment-embarcaciones-check'
    case NotificationID.SOL_INS_TRI:
    case NotificationID.SOL_MODIF_TRI:
      return 'enrollment-tripulantes-check'
    case NotificationID.SOL_INS_PROV:
    case NotificationID.SOL_MODIF_PROV:
      return 'enrollment-proveedores-check'
    case NotificationID.SOL_SERVICIO:
    case NotificationID.NOTIF_TARIFA_ESPECIAL:
      return 'servicios-check'
    case NotificationID.SOL_VIAJE_PREACR:
      return 'preacreditacion'
    case NotificationID.NOTIF_SERV_DISPONIBLE:
    case NotificationID.NOTIF_VIAJE_DISPONIBLE:
    case NotificationID.NOTIF_CAMBIO_MUELLE:
      return 'control'
    case NotificationID.RESP_INS_ARM:
    case NotificationID.RESP_MODIF_ARM:
      return 'enrollment-armadores'
    case NotificationID.RESP_INS_EMB:
    case NotificationID.RESP_MODIF_EMB:
      return 'enrollment-embarcaciones'
    case NotificationID.RESP_INS_TRI:
    case NotificationID.RESP_MODIF_TRI:
      return 'enrollment-tripulantes'
    case NotificationID.RESP_INS_PROV:
    case NotificationID.RESP_MODIF_PROV:
      return 'enrollment-proveedores'
    case NotificationID.RESP_SOL_SERVICIO:
      return 'services'
    case NotificationID.RESP_PREACR:
    case NotificationID.NOTIF_CAMBIO_MUELLE_2:
    case NotificationID.RESP_SOL_MUELLE_WESTE:
      return 'viajes'
    case NotificationID.ALERTA_COBRO:
      return 'finanzas-check'
    case NotificationID.SOL_REP:
      return 'enrollment-representantes-check'
    case NotificationID.RESP_REP:
      return 'preferences'
    default:
      return 'dashboard'
  }
}

export enum NotificationType {
  GENERAL = 'general',
  USUARIO = 'usuario',
}

export enum NotificationStatus {
  PENDING = 0,
  READED = 1,
}

export interface INotification {
  id: number
  tipo: NotificationType
  id_notificacion: number
  receptor: string
  mensaje: string
  estado: NotificationStatus
  fecha_creacion: string
}

export default class NotificacionesServices {
  private bucketID: string | null = null

  constructor() {
    // Registrar el bucket para cambios
    this.bucketID = dataStorageManager.registerBucket<INotification>(
      this.getAllNotifications.bind(this), // Asegúrate de enlazar el contexto correcto
      {
        refreshRatio: /*1 * 60*/ 10 * 1000, // Refrescar cada minuto
        //preload: true, // Preload
        bucketID: BucketDefaultIDs.NOTIFICATIONS, // ID del bucket
        operations: new Map<string, DMS_Operation>([
          [
            'updateCambio',
            {
              operation: this.updateCambio.bind(this),
              reloadAfter: true,
            },
          ],
        ]), // Las operaciones se añadiran despues
      },
    )
  }

  startup() {}

  async getAllNotifications(): Promise<INotification[]> {
    try {
      const response = await dataQueryManager.addQuery<INotification | INotification[]>({
        query: `${import.meta.env.VITE_BASE_URL}/dashboard/1/op_select_notificaciones`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
      })
      if (response.data && Array.isArray(response.data)) {
        return response.data
      } else if (response.data) {
        return [response.data]
      }
    } catch (error) {
      console.error('Error fetching notifications:', error)
    }
    return []
  }

  async updateCambio(cambioData: INotification): Promise<boolean> {
    try {
      const body = {
        op_update_notificaciones: {
          tipo: cambioData.tipo,
          receptor: cambioData.receptor,
          mensaje: cambioData.mensaje,
          estado: cambioData.estado,
          id: cambioData.id,
        },
      }

      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/dashboard/1/op_update_notificaciones`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'number') {
        return true
      }
    } catch (error) {
      console.error('Error updating cambios:', error)
    }
    return false
  }
}
